import { useEffect } from "react";
import { Bell, Heart, Search, ShoppingCart, UserCircle2 } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/shared/ui";

interface Props {
  isAuthenticated: boolean;
  dashboardHref: string;
  canonicalUrl?: string;
}

export const SmartHeader = ({ isAuthenticated, dashboardHref, canonicalUrl }: Props): JSX.Element => {
  const { pathname } = useLocation();
  const canonical = canonicalUrl ?? (typeof window !== "undefined" ? `${window.location.origin}${pathname}` : "");

  useEffect(() => {
    if (!canonical) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonical;
    return () => { link?.remove(); };
  }, [canonical]);

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-card/95 px-4 py-3 shadow-sm backdrop-blur sm:px-6 lg:px-10">
      <div className="mx-auto flex max-w-7xl items-center gap-4">

        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-primary/10">
            <Heart className="h-4 w-4 text-primary" fill="currentColor" aria-hidden="true" />
          </span>
          <span className="text-lg font-bold text-primary">eshëndetësia</span>
        </Link>

        {/* Search kompakt */}
        <div className="mx-auto hidden max-w-xs flex-1 items-center rounded-full border border-border bg-background px-4 py-2 sm:flex lg:max-w-sm">
          <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <Link to="/search" className="flex-1 text-sm text-muted-foreground hover:text-slate-600">Kërko...</Link>
        </div>

        {/* Nav */}
        <nav className="hidden items-center gap-3 text-sm font-medium text-muted-foreground lg:flex">
          <HeaderIconLink to="/app/favorites" icon={<Heart className="h-5 w-5" />} label="Preferencat" />
          <HeaderIconLink to="/app/cart" icon={<ShoppingCart className="h-5 w-5" />} label="Shporta" />
          <Link to="/about" className="ml-2 transition hover:text-primary">Rreth nesh</Link>
        </nav>

        {/* Ikona + Auth */}
        <div className="ml-auto flex items-center gap-1.5">
          {isAuthenticated ? (
            <>
              <IconBtn icon={<Bell  className="h-5 w-5" />} badge label="Njoftimet" />
              <IconBtn icon={<Heart className="h-5 w-5" />} badge label="Preferencat" />
              <IconBtn icon={<ShoppingCart className="h-5 w-5" />} badge label="Shporta" />
              <Link to={dashboardHref} aria-label="Profili im"
                className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-primary transition hover:bg-[#D4EAFD]">
                <UserCircle2 className="h-5 w-5" aria-hidden="true" />
              </Link>
            </>
          ) : (
            <Button asChild className="h-9 rounded-full border border-primary/30 bg-transparent px-5 text-sm text-primary hover:bg-primary/10">
              <Link to="/login">Kyçu</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

function HeaderIconLink({ to, icon, label }: { to: string; icon: JSX.Element; label: string }): JSX.Element {
  return (
    <Link
      to={to}
      aria-label={label}
      title={label}
      className="relative grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-primary"
    >
      {icon}
    </Link>
  );
}

function IconBtn({ icon, badge, label }: { icon: JSX.Element; badge?: boolean; label: string }): JSX.Element {
  return (
    <button type="button" aria-label={label}
      className="relative grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition hover:bg-muted">
      {icon}
      {badge && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-rose-500" aria-hidden="true" />}
    </button>
  );
}
